import { modifier } from 'ember-modifier';
import { isNuovaDocument } from '../lib/nv-layer-stack.js';

/**
 * MAP (in-file navigation graph — keep in sync with the code):
 *   role:      modifier — «UI nuova» dialog focus (docs/ui-nuova/components/overlays.md §2): focus IN on open,
 *              Tab / Shift+Tab TRAP inside the frame, focus RETURN to the opener on close. A no-op outside nuova
 *              (html[data-ui='nuova']): classic keeps its focus behaviour byte-identical.
 *   usage:     on the dialog element itself (not a backdrop): `{{nv-dialog-focus body='.x-body'}}`;
 *              `initial='self'` focuses the frame instead of a field (record popups: a full form must not open
 *              with a field focused); `enabled=@showModal` for an element that stays mounted while closed
 *              (BbPanel, as a boolean) — falsy = no-op, a false→true flip is an open, true→false a close.
 *              NOT on BbModal record popups: their forms commit on focusout (invoice lines), and a frame focus
 *              that blurs a field mid-edit broke the invoice line flow (bisected 2026-10-21).
 *   named:     body (selector of the region whose first visible focusable takes the focus; default: the whole
 *              element), initial ('self' | undefined), enabled (default true)
 *   uses:      lib/nv-layer-stack (isNuovaDocument)
 *   siblings:  beebee-base-template components/nv-sheet.js (same FOCUSABLE rule, its own focus code);
 *              components/bb-modal-modern, bb-modal, bb-panel (consumers)
 *   tests:     tests/integration/modifiers/nv-dialog-focus-test.js
 *   gotchas:   (1) The frame can be display:none for a few frames after insert (BbModal `.modal-container.out`
 *              for 50ms, BbPanel `.hide` for 50ms): the initial focus retries on animation frames until the
 *              element is laid out, MAX_FRAMES at most, and gives up if the user moved the focus elsewhere
 *              meanwhile. (2) Coarse pointer (touch): the frame itself, never a text field — the keyboard
 *              would pop over the dialog. (3) Escape is NOT handled here: each component owns it through
 *              lib/nv-layer-stack (top layer only, busy guard). (4) A nested dialog's Tab handler runs first and
 *              prevents default: the outer one skips a prevented event. (5) At teardown Glimmer may already
 *              have detached the element: a focus on <body> or on a detached node counts as «lost here»; the
 *              return runs on the next task, after the frame left the DOM (no focusout on a field inside).
 *              (6) The opener is the focus at install, or — when a child already focused itself — the last
 *              focus outside every [data-nv-dialog] (document focusin capture listener).
 */

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// ~0.6s at 60fps: longer than every open animation gate (BbModal 50ms, BbPanel 50ms + 150ms).
const MAX_FRAMES = 40;
function visibleFocusables(root) {
  if (!root?.querySelectorAll) return [];
  return [...root.querySelectorAll(FOCUSABLE)].filter(el => el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden' && !el.closest('[inert], [aria-hidden="true"]'));
}

// The last element focused OUTSIDE every open dialog (one capture listener, installed with the first dialog): the
// opener when a child of the dialog focused itself before this modifier ran (child modifiers install first:
// BbUnlinkConfirm, {{autofocus}} forms, search inputs).
let lastOutside = null;
let tracking = false;
function trackOutsideFocus() {
  if (tracking) return;
  tracking = true;
  document.addEventListener('focusin', event => {
    const target = event.target;
    if (target instanceof Element && !target.closest('[data-nv-dialog]')) lastOutside = target;
  }, true);
}
function isCoarsePointer() {
  try {
    return window.matchMedia?.('(pointer: coarse)')?.matches === true;
  } catch {
    return false;
  }
}
function focusQuietly(el) {
  try {
    el.focus({
      preventScroll: true
    });
  } catch {
    el.focus?.();
  }
}
var nvDialogFocus = modifier((element, _positional, named = {}) => {
  const {
    body,
    initial
  } = named;
  // `enabled` absent = on; present = its truthiness (BbPanel passes a boolean derived from @showModal)
  const enabled = 'enabled' in named ? Boolean(named.enabled) : true;
  if (!isNuovaDocument() || !enabled) return;
  trackOutsideFocus();
  element.setAttribute('data-nv-dialog', '');
  const active0 = document.activeElement;
  const opener = active0 && element.contains(active0) ? lastOutside : active0;
  const addedTabindex = !element.hasAttribute('tabindex');
  if (addedTabindex) element.setAttribute('tabindex', '-1');

  // Focus in. The focus may be elsewhere on purpose by the time the frame is laid out: only the opener (still
  // focused), <body> / nothing, or a detached node count as «not moved by the user».
  const focusIsFree = () => {
    const active = document.activeElement;
    return !active || active === document.body || active === opener || !active.isConnected;
  };
  // next frame; a hidden document (background tab) runs no animation frames, so a timer takes over there
  let frame = null;
  let frameIsTimer = false;
  let frames = 0;
  const nextFrame = fn => {
    frameIsTimer = document.hidden;
    frame = frameIsTimer ? setTimeout(fn, 16) : requestAnimationFrame(fn);
  };
  const focusIn = () => {
    frame = null;
    if (!element.isConnected) return;
    if (element.contains(document.activeElement)) return;
    if (!focusIsFree()) return;
    if (element.getClientRects().length === 0) {
      if (++frames < MAX_FRAMES) nextFrame(focusIn);
      return;
    }
    let target = null;
    if (initial !== 'self' && !isCoarsePointer()) {
      const scope = body ? element.querySelector(body) : element;
      target = visibleFocusables(scope)[0] ?? null;
    }
    focusQuietly(target ?? element);
  };
  nextFrame(focusIn);

  // Trap: Tab / Shift+Tab cycle inside the frame (visible focusables only).
  const onKeydown = event => {
    if (event.key !== 'Tab' || event.defaultPrevented) return;
    const items = visibleFocusables(element);
    if (items.length === 0) {
      event.preventDefault();
      focusQuietly(element);
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    const outside = !element.contains(active);
    if (event.shiftKey && (outside || active === first || active === element)) {
      event.preventDefault();
      focusQuietly(last);
    } else if (!event.shiftKey && (outside || active === last)) {
      event.preventDefault();
      focusQuietly(first);
    }
  };
  element.addEventListener('keydown', onKeydown);
  return () => {
    if (frame !== null) (frameIsTimer ? clearTimeout : cancelAnimationFrame)(frame);
    frame = null;
    element.removeEventListener('keydown', onKeydown);
    if (addedTabindex && element.getAttribute('tabindex') === '-1') element.removeAttribute('tabindex');
    element.removeAttribute('data-nv-dialog');
    // Return: only when the focus was inside the frame or got lost with it — and only AFTER the frame left the DOM:
    // moving the focus now would fire `focusout` on the field still focused inside, and forms commit on focusout
    // (invoice lines: updateInvoice) — a close must never commit an edit.
    const active = document.activeElement;
    const wasHere = !active || active === document.body || !active.isConnected || element.contains(active);
    if (!wasHere || !opener || opener === document.body || typeof opener.focus !== 'function') return;
    setTimeout(() => {
      const now = document.activeElement;
      const lost = !now || now === document.body || !now.isConnected;
      if (lost && opener.isConnected) focusQuietly(opener);
    }, 0);
  };
});

export { nvDialogFocus as default };
//# sourceMappingURL=nv-dialog-focus.js.map
